import React, { useState, useEffect } from "react";
import { REQUEST_URL } from "../../config/urls";

const WEBLOJA_API_KEY = process.env.REACT_APP_WEBLOJA_API_KEY || "";

function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatarData(dataStr) {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function TabelaComprasWebloja({ idOrc }) {
  const [compras, setCompras] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => {
    if (!idOrc) return;
    setCarregando(true);
    setErro(null);

    fetch(`${REQUEST_URL.replace("/mapavendas", "")}/webloja/compras/por-orcamento/${idOrc}`, {
      headers: { "x-api-key": WEBLOJA_API_KEY },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Erro ${res.status}`);
        return res.json();
      })
      .then((dados) => {
        setCompras(Array.isArray(dados) ? dados : []);
        setCarregando(false);
      })
      .catch((err) => {
        setErro("Não foi possível carregar as compras da webloja.");
        setCarregando(false);
        console.error("[TabelaComprasWebloja]", err);
      });
  }, [idOrc]);

  function toggleExpandir(id) {
    setExpandidos((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  if (carregando) {
    return <p style={{ padding: 16, color: "#666" }}>Carregando compras da webloja...</p>;
  }

  if (erro) {
    return <p style={{ padding: 16, color: "#c0392b" }}>{erro}</p>;
  }

  if (compras.length === 0) {
    return (
      <p style={{ padding: 16, color: "#888" }}>
        Nenhuma compra realizada na webloja para este contrato.
      </p>
    );
  }

  const totalGeral = compras.reduce((acc, c) => acc + Number(c.total || 0), 0);

  return (
    <div style={{ marginTop: 8 }}>
      <strong style={{ fontSize: 13, color: "#495057" }}>Compras Webloja</strong>
      <div style={{ border: "1px solid #dee2e6", borderRadius: 6, overflow: "hidden", marginTop: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#e9ecef" }}>
              <th style={{ padding: "6px 12px", textAlign: "left", fontWeight: 600, width: 32 }}></th>
              <th style={{ padding: "6px 12px", textAlign: "left", fontWeight: 600 }}>Pedido</th>
              <th style={{ padding: "6px 12px", textAlign: "left", fontWeight: 600 }}>Comprador</th>
              <th style={{ padding: "6px 12px", textAlign: "left", fontWeight: 600 }}>Data</th>
              <th style={{ padding: "6px 12px", textAlign: "left", fontWeight: 600 }}>Itens</th>
              <th style={{ padding: "6px 12px", textAlign: "right", fontWeight: 600 }}>Total</th>
              <th style={{ padding: "6px 12px", textAlign: "center", fontWeight: 600 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {compras.map((compra, i) => {
              const aberto = !!expandidos[compra.id];
              const items = Array.isArray(compra.items) ? compra.items : [];
              const resumoItens = items.length === 0
                ? "—"
                : items.slice(0, 2).map(it => `${it.name || "Produto"} x${it.quantity}`).join(", ") + (items.length > 2 ? ` +${items.length - 2}` : "");

              return (
                <React.Fragment key={compra.id}>
                  <tr
                    style={{ borderTop: "1px solid #dee2e6", background: i % 2 === 0 ? "#fff" : "#f8f9fa", cursor: items.length ? "pointer" : "default" }}
                    onClick={() => items.length && toggleExpandir(compra.id)}
                  >
                    <td style={{ padding: "6px 12px", textAlign: "center", color: "#6c757d" }}>
                      {items.length > 0 && (
                        <span style={{ fontSize: 11 }}>{aberto ? "▲" : "▼"}</span>
                      )}
                    </td>
                    <td style={{ padding: "6px 12px", fontFamily: "monospace", fontSize: 12 }}>{compra.orderCode}</td>
                    <td style={{ padding: "6px 12px" }}>
                      {/* Só o nome: o CPF sozinho não diz nada para quem
                          consulta. Fica "—" quando a loja não enviou o nome e
                          o CPF não bate com nenhum contato cadastrado. */}
                      {compra.studentName || "—"}
                    </td>
                    <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }}>{formatarData(compra.createdAt)}</td>
                    <td style={{ padding: "6px 12px", color: "#6c757d", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {resumoItens}
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 600 }}>{formatarMoeda(compra.total)}</td>
                    <td style={{ padding: "6px 12px", textAlign: "center" }}>
                      <span style={{
                        background: compra.status === "pago" ? "#27ae60" : "#e67e22",
                        color: "#fff",
                        borderRadius: 12,
                        padding: "2px 10px",
                        fontSize: 11,
                        textTransform: "capitalize",
                      }}>
                        {compra.status}
                      </span>
                    </td>
                  </tr>

                  {aberto && (
                    <tr style={{ background: "#f1f3f5" }}>
                      <td colSpan={7} style={{ padding: "0 12px 10px 44px" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 }}>
                          <thead>
                            <tr style={{ background: "#e2e6ea" }}>
                              <th style={{ padding: "4px 8px", textAlign: "left" }}>Produto</th>
                              <th style={{ padding: "4px 8px", textAlign: "center" }}>Qtd</th>
                              <th style={{ padding: "4px 8px", textAlign: "right" }}>Unit.</th>
                              <th style={{ padding: "4px 8px", textAlign: "right" }}>Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((it, j) => (
                              <tr key={j} style={{ borderTop: "1px solid #dee2e6" }}>
                                <td style={{ padding: "4px 8px" }}>{it.name || "Produto"}</td>
                                <td style={{ padding: "4px 8px", textAlign: "center" }}>{it.quantity}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right" }}>{formatarMoeda(it.price)}</td>
                                <td style={{ padding: "4px 8px", textAlign: "right" }}>{formatarMoeda(it.subtotal)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid #dee2e6", background: "#f8f9fa" }}>
              <td colSpan={6} style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700 }}>
                Total geral:
              </td>
              <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#27ae60", whiteSpace: "nowrap" }}>
                {formatarMoeda(totalGeral)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export { TabelaComprasWebloja };
